import dayjs from 'dayjs'
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { DEFAULT_APP_LANGUAGE } from '../utils/enums'
import {
	addLanguageToPath,
	determineInitialLanguage,
	getAvailableLanguages,
	getDayjsLocale,
	hasLanguagePrefix,
	isLanguageSupported,
	removeLanguageFromPath,
	storeLanguagePreference,
	validateLanguage
} from '../utils/languageUtils'

/**
 * Language context type definition
 */
interface LanguageContextType {
	/** Current active language */
	currentLanguage: string
	/** Array of all available/supported languages */
	availableLanguages: string[]
	/** Function to change the current language */
	changeLanguage: (language: string) => Promise<void>
	/** Function to set language from external source (e.g., onboarding data) */
	setLanguageFromSource: (language: string, source: 'onboarding' | 'user') => Promise<void>
	/** Loading state during language changes */
	isLanguageLoading: boolean
	/** Function to get a localized path with language prefix */
	getLocalizedPath: (path: string) => string
	/** Function to get path without language prefix */
	getPathWithoutLanguage: (path: string) => string
	/** Check if language context is initialized */
	isInitialized: boolean
}

/**
 * Language context - undefined when not within provider
 */
const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

/**
 * Props for LanguageProvider component
 */
interface LanguageProviderProps {
	children: ReactNode
}

/**
 * LanguageProvider component that manages language state and provides context
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
	const [currentLanguage, setCurrentLanguage] = useState<string>(DEFAULT_APP_LANGUAGE)
	const [isLanguageLoading, setIsLanguageLoading] = useState(true)
	const [isInitialized, setIsInitialized] = useState(false)
	const [languageSource, setLanguageSource] = useState<'initial' | 'onboarding' | 'user'>('initial')

	const { i18n } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()

	const availableLanguages = getAvailableLanguages()

	/**
	 * Get localized path with current language prefix
	 */
	const getLocalizedPath = useCallback(
		(path: string): string => {
			return addLanguageToPath(path, currentLanguage)
		},
		[currentLanguage]
	)

	/**
	 * Get path without language prefix
	 */
	const getPathWithoutLanguage = useCallback((path: string): string => {
		return removeLanguageFromPath(path)
	}, [])

	/**
	 * Internal change language function with configurable localStorage storage
	 */
	const changeLanguageInternal = useCallback(
		async (language: string, storePreference: boolean): Promise<void> => {
			const validatedLanguage = validateLanguage(language)

			if (validatedLanguage === currentLanguage) {
				return // No change needed
			}

			setIsLanguageLoading(true)

			try {
				// Update i18next language
				await i18n.changeLanguage(validatedLanguage)

				// Update dayjs locale
				const dayjsLocale = getDayjsLocale(validatedLanguage)
				dayjs.locale(dayjsLocale)

				// Update state
				setCurrentLanguage(validatedLanguage)

				// Store preference in localStorage only if requested
				if (storePreference) {
					storeLanguagePreference(validatedLanguage)
				}

				// Update URL with new language prefix, preserving query parameters and hash
				const currentPath = location.pathname
				const currentSearch = location.search
				const currentHash = location.hash
				const pathWithoutLang = removeLanguageFromPath(currentPath)
				const newPath = addLanguageToPath(pathWithoutLang, validatedLanguage)
				const fullNewUrl = `${newPath}${currentSearch}${currentHash}`
				const fullCurrentUrl = `${currentPath}${currentSearch}${currentHash}`

				// Only navigate if the URL actually changed
				if (fullCurrentUrl !== fullNewUrl) {
					navigate(fullNewUrl, { replace: true })
				}
			} catch (error) {
				console.error('Failed to change language:', error)
				// Revert to previous language on error
				setCurrentLanguage(currentLanguage)
			} finally {
				setIsLanguageLoading(false)
			}
		},
		[currentLanguage, i18n, location.hash, location.pathname, location.search, navigate]
	)

	/**
	 * Change language function with full integration
	 */
	const changeLanguage = useCallback(
		async (language: string): Promise<void> => {
			setLanguageSource('user')
			await changeLanguageInternal(language, true)
		},
		[changeLanguageInternal]
	)

	/**
	 * Set language from external source (onboarding data, etc.)
	 */
	const setLanguageFromSource = useCallback(
		async (language: string, source: 'onboarding' | 'user'): Promise<void> => {
			const validatedLanguage = validateLanguage(language)

			if (validatedLanguage === currentLanguage) return

			// Only allow onboarding to override initial language, not user choices
			if (languageSource === 'user' && source === 'onboarding') {
				return // Don't override user's explicit choice
			}

			setLanguageSource(source)

			await changeLanguageInternal(validatedLanguage, source === 'user')
		},
		[currentLanguage, languageSource, changeLanguageInternal]
	)

	/**
	 * Initialize language on component mount
	 */
	useEffect(() => {
		const initializeLanguage = async () => {
			try {
				setIsLanguageLoading(true)

				// Determine initial language based on URL, localStorage, and browser
				const initialLanguage = determineInitialLanguage(location.pathname)

				// Check if URL already has the correct language prefix, preserving query parameters and hash
				const currentPath = location.pathname
				const currentSearch = location.search
				const currentHash = location.hash
				const hasPrefix = hasLanguagePrefix(currentPath)
				const expectedPath = addLanguageToPath(hasPrefix ? removeLanguageFromPath(currentPath) : currentPath, initialLanguage)
				const fullCurrentUrl = `${currentPath}${currentSearch}${currentHash}`
				const fullExpectedUrl = `${expectedPath}${currentSearch}${currentHash}`

				// Update i18next
				await i18n.changeLanguage(initialLanguage)

				// Update dayjs locale
				const dayjsLocale = getDayjsLocale(initialLanguage)
				dayjs.locale(dayjsLocale)

				// Update state
				setCurrentLanguage(initialLanguage)
				setLanguageSource('initial')

				// Store preference
				storeLanguagePreference(initialLanguage)

				// Navigate to correct URL if needed
				if (fullCurrentUrl !== fullExpectedUrl) {
					navigate(fullExpectedUrl, { replace: true })
				}

				setIsInitialized(true)
			} catch (error) {
				console.error('Failed to initialize language:', error)
				// Fallback to default language
				setCurrentLanguage(DEFAULT_APP_LANGUAGE)
				setIsInitialized(true)
			} finally {
				setIsLanguageLoading(false)
			}
		}

		initializeLanguage()
	}, [i18n, location.hash, location.pathname, location.search, navigate]) // Only run on mount

	/**
	 * Handle URL changes to sync language state
	 */
	useEffect(() => {
		if (!isInitialized) return

		const urlLanguage = determineInitialLanguage(location.pathname)

		// If URL language differs from current language, update current language
		if (urlLanguage !== currentLanguage && isLanguageSupported(urlLanguage)) {
			setCurrentLanguage(urlLanguage)

			// Update i18next and dayjs without navigation (URL already changed)
			i18n.changeLanguage(urlLanguage).catch(console.error)
			dayjs.locale(getDayjsLocale(urlLanguage))
			storeLanguagePreference(urlLanguage)
		}
	}, [location.pathname, currentLanguage, isInitialized, i18n])

	/**
	 * Context value object
	 */
	const contextValue: LanguageContextType = useMemo(
		() => ({
			currentLanguage,
			availableLanguages,
			changeLanguage,
			setLanguageFromSource,
			isLanguageLoading,
			getLocalizedPath,
			getPathWithoutLanguage,
			isInitialized
		}),
		[currentLanguage, availableLanguages, changeLanguage, setLanguageFromSource, isLanguageLoading, getLocalizedPath, getPathWithoutLanguage, isInitialized]
	)

	return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
}

/**
 * Custom hook to consume LanguageContext
 * Throws error if used outside of LanguageProvider
 */
export const useLanguageContext = (): LanguageContextType => {
	const context = useContext(LanguageContext)

	if (context === undefined) {
		throw new Error('useLanguageContext must be used within a LanguageProvider')
	}

	return context
}

/**
 * Type exports for external use
 */
export type { LanguageContextType, LanguageProviderProps }
/**
 * Export context for advanced use cases
 */
export { LanguageContext }
